/**
 * Service Desk — centralized API layer (same pattern as A1 `src/services/APIs/`).
 * All HTTP calls for the service-desk project should go through this service.
 */
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { axiosConfig } from "@/services/axiosConfig";
import {
  DynamicModuleService,
  SchemaService,
  UploadService,
} from "@/services/APIs";
import type { DynamicPayload } from "@/utils/types/dynamicModule";
import { SERVICE_DESK_FORMILY_ENDUSER_MODEL } from "./serviceDeskConstants";

export const apiClient = axiosConfig;

const SD_REST_BASE = "/service-desk";
const FORMILY_BASE = "/formily/dynamic-model";

/**
 * Serialize query params, expanding array values into repeated keys
 * (e.g. `project=uuid1&project=uuid2`) so the PTS backend can read multiple
 * projects via `getlist`. Non-array commas are kept literal.
 */
function multiValueParamsSerializer(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    const encodedKey = encodeURIComponent(key);
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === "") continue;
        parts.push(`${encodedKey}=${encodeURIComponent(String(item))}`);
      }
    } else {
      parts.push(
        `${encodedKey}=${encodeURIComponent(String(value)).replace(/%2C/gi, ",")}`
      );
    }
  }
  return parts.join("&");
}

const MULTI_VALUE_CONFIG: AxiosRequestConfig = {
  paramsSerializer: { serialize: multiValueParamsSerializer },
};

/** Split a comma-joined `project` param into an array for repeated-key serialization. */
function expandProjectParam(
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!params) return params;
  const project = params.project;
  if (typeof project === "string" && project.includes(",")) {
    return {
      ...params,
      project: project
        .split(",")
        .map(p => p.trim())
        .filter(Boolean),
    };
  }
  return params;
}

function encodeModelName(modelName: string): string {
  return encodeURIComponent(modelName.trim());
}

function formilyRecordPath(modelName: string, id?: string): string {
  const base = `${FORMILY_BASE}/${encodeModelName(modelName)}`;
  return id ? `${base}/${encodeURIComponent(id)}/` : `${base}/`;
}

const BLOB_GET_HEADERS = {
  Accept: "application/pdf, application/octet-stream, video/webm, video/*, */*",
};

class ServiceDeskAPIService {
  /** Shared axios instance (tenant, auth, refresh). */
  readonly client = apiClient;

  auth = {
    login: (credentials: { email: string; password: string }) =>
      apiClient.post("/auth/login/", credentials),

    signup: (credentials: {
      email: string;
      password: string;
      first_name: string;
      last_name: string;
    }) => apiClient.post("/auth/", credentials),

    logout: (refresh: string) =>
      apiClient.post("/auth/logout/", { refresh }),

    getCurrentRole: (userId: string) =>
      apiClient.get(`/service_desk/user-current-role/?user_id=${userId}`),

    changePassword: (
      data: { current_password: string; new_password: string },
      config?: AxiosRequestConfig
    ) =>
      apiClient.post("/auth/password/change/", data, {
        ...config,
        params: { is_pts: false, ...config?.params },
      }),

    forgotPasswordRequest: (
      data: { email: string; redirect_url?: string },
      config?: AxiosRequestConfig
    ) =>
      apiClient.post("/forgot-password-request/", data, {
        ...config,
        params: { is_pts: false, ...config?.params },
      }),

    forgotPasswordReset: (
      id: string,
      data: { new_password: string; confirm_password: string },
      config?: AxiosRequestConfig
    ) =>
      apiClient.post(
        `/forgot-password-reset/${encodeURIComponent(id)}/`,
        data,
        {
          ...config,
          params: { is_pts: false, ...config?.params },
        }
      ),
  };

  tickets = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get(`${SD_REST_BASE}/tickets/`, { params }),

    get: (id: string) => apiClient.get(`${SD_REST_BASE}/tickets/${id}/`),

    create: (data: Record<string, unknown>) =>
      apiClient.post(`${SD_REST_BASE}/tickets/`, data),

    update: (id: string, data: Record<string, unknown>) =>
      apiClient.patch(`${SD_REST_BASE}/tickets/${id}/`, data),

    delete: (id: string) => apiClient.delete(`${SD_REST_BASE}/tickets/${id}/`),

    createWithFile: (formData: FormData) =>
      apiClient.post("/service_desk/ticket_create/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),

    /** List/filter tickets — supports `?project={uuid}` and other query params. */
    listCreatedTickets: (
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) => apiClient.get("/service_desk/ticket_create/", { params, ...config }),

    /** GET ticket detail for edit — includes PTS fields (Formily GET often omits them). */
    getCreatedTicket: (id: string, config?: AxiosRequestConfig) =>
      apiClient.get(
        `/service_desk/ticket_create/${encodeURIComponent(id)}/`,
        config
      ),

    /** PATCH ticket update — same form-data shape as create. */
    updateCreatedTicket: (id: string, formData: FormData, config?: AxiosRequestConfig) =>
      apiClient.patch(
        `/service_desk/ticket_create/${encodeURIComponent(id)}/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          ...config,
        }
      ),

    /** PATCH ticket fields (JSON) — e.g. `assigned_to`, `status`. */
    patchCreatedTicket: (
      id: string,
      data: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) =>
      apiClient.patch(
        `/service_desk/ticket_create/${encodeURIComponent(id)}/`,
        data,
        config
      ),

    /** DELETE ticket — same resource as create/edit. */
    deleteCreatedTicket: (id: string, config?: AxiosRequestConfig) =>
      apiClient.delete(
        `/service_desk/ticket_create/${encodeURIComponent(id)}/`,
        config
      ),

    /** POST bulk ticket import — multipart `file` upload (xlsx template). */
    bulkImportTickets: (formData: FormData, config?: AxiosRequestConfig) =>
      apiClient.post("/service_desk/ticket_bulk_import/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        ...config,
      }),

    /** POST bulk ticket update — `{ ids, status?, assigned_to? }` in one call. */
    bulkUpdateTickets: (
      data: {
        ids: string[];
        status?: string;
        assigned_to?: string | null;
      },
      config?: AxiosRequestConfig
    ) =>
      apiClient.post("/service_desk/ticket_bulk_update/", data, config),
  };

  assets = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get(`${SD_REST_BASE}/assets/`, { params }),

    get: (id: string) => apiClient.get(`${SD_REST_BASE}/assets/${id}/`),

    create: (data: Record<string, unknown>) =>
      apiClient.post(`${SD_REST_BASE}/assets/`, data),

    update: (id: string, data: Record<string, unknown>) =>
      apiClient.patch(`${SD_REST_BASE}/assets/${id}/`, data),
  };

  knowledgeBase = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get(`${SD_REST_BASE}/knowledge-base/`, { params }),

    get: (id: string) =>
      apiClient.get(`${SD_REST_BASE}/knowledge-base/${id}/`),

    create: (data: Record<string, unknown>) =>
      apiClient.post(`${SD_REST_BASE}/knowledge-base/`, data),
  };

  reports = {
    /** GET Tracker matrix — multi-project via repeated `?project=uuid1&project=uuid2`. */
    tracker: (
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get("/service_desk/reports/", {
        params: expandProjectParam(params),
        ...MULTI_VALUE_CONFIG,
        ...config,
      }),

    summary: (params?: Record<string, unknown>) =>
      apiClient.get(`${SD_REST_BASE}/reports/summary/`, { params }),

    tickets: (params?: Record<string, unknown>) =>
      apiClient.get(`${SD_REST_BASE}/reports/tickets/`, { params }),
  };

  users = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get(`${SD_REST_BASE}/users/`, { params }),

    get: (id: string) => apiClient.get(`${SD_REST_BASE}/users/${id}/`),
  };

  employees = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get("/service_desk/employee_list/", { params }),

    update: (data: Record<string, unknown>) =>
      apiClient.patch("/service_desk/employee_list/", data),
  };

  agentEmployees = {
    list: (config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/agent_employee_list/", config),
  };

  projectAssigned = {
    list: (projectId: string, config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/project-assigned/", {
        params: { project_id: projectId },
        ...config,
      }),
  };

  /** External Agent / External Customer users for Settings → External User Access. */
  externalUsers = {
    list: (config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/external_user_list/", config),
  };

  /** Assign / list projects for an external user. */
  userProjects = {
    list: (userId?: string, config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/user_project/", {
        params: userId ? { user_id: userId } : undefined,
        ...config,
      }),

    save: (
      data: { user: string; projects: string[] },
      config?: AxiosRequestConfig
    ) =>
      apiClient.post("/service_desk/user_project/", data, {
        skipErrorNotification: true,
        ...config,
      }),

    update: (
      mappingId: string,
      data: { user: string; projects: string[] },
      config?: AxiosRequestConfig
    ) =>
      apiClient.put(
        `/service_desk/user_project/${encodeURIComponent(mappingId)}/`,
        data,
        { skipErrorNotification: true, ...config }
      ),

    remove: (mappingId: string, config?: AxiosRequestConfig) =>
      apiClient.delete(
        `/service_desk/user_project/${encodeURIComponent(mappingId)}/`,
        { skipErrorNotification: true, ...config }
      ),
  };

  /** Agent panel — projects assigned to the logged-in employee. */
  agentAssignedProjects = {
    list: (empId?: string, config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/agent_assigned_projects/", {
        params: empId ? { emp_id: empId } : undefined,
        ...config,
      }),
  };

  endusers = {
    listAll: (page = 1, pageSize = 200, config?: AxiosRequestConfig) =>
      apiClient.get(
        `${FORMILY_BASE}/${encodeModelName(SERVICE_DESK_FORMILY_ENDUSER_MODEL)}/grid_data/`,
        {
          params: { page, page_size: pageSize },
          ...config,
        }
      ),
  };

  kanban = {
    view: (params?: Record<string, unknown>) =>
      apiClient.get("/service_desk/kanban-view/", { params }),
  };

  roles = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get("/tenant-permissions/roles/", { params }),
  };

  dashboard = {
    /**
     * GET admin dashboard payload. Optional `date` (`DD/MM/YYYY`) and `project`
     * (comma-joined uuids) filter the `status_activity` counters and lists.
     * `MULTI_VALUE_CONFIG` keeps the comma literal (`project=uuid1,uuid2`).
     */
    main: (params?: Record<string, unknown>, config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/main-dashboard/", {
        params,
        ...MULTI_VALUE_CONFIG,
        ...config,
      }),

    /** GET status counts for dashboard project status page. */
    statusDashboard: (
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get("/service_desk/status_dashboard/", { params, ...config }),

    /**
     * GET per-agent ticket counts (in-progress / assigned) for the
     * "Agents — Most In Progress" page. Requires `project`; supports multiple
     * projects via repeated `?project=uuid1&project=uuid2`. Optional
     * `assigned_to` narrows to a single agent.
     */
    agentStatus: (
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get("/service_desk/agent_status/", {
        params: expandProjectParam(params),
        ...MULTI_VALUE_CONFIG,
        ...config,
      }),
  };

  activityLogs = {
    list: (
      params?: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get("/service_desk/activity-logs/", {
        ...config,
        params,
      }),
  };

  /** Per-ticket audit trail for read-only detail page History tab. */
  ticketHistory = {
    list: (
      params: { ticket_id: string; page?: number; page_size?: number },
      config?: AxiosRequestConfig
    ) =>
      apiClient.get("/service_desk/ticket_history/", {
        ...config,
        params,
      }),
  };

  /** User comments on a ticket — Redmine-style Notes tab. */
  ticketNotes = {
    list: (
      params: { ticket_id: string; page?: number; page_size?: number },
      config?: AxiosRequestConfig
    ) =>
      apiClient.get("/service_desk/ticket_notes/", {
        ...config,
        params,
      }),

    create: (
      data: { ticket_id: string; note: string },
      config?: AxiosRequestConfig
    ) => apiClient.post("/service_desk/ticket_notes/", data, config),
  };

  mergeTickets = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get("/service_desk/merge_tickets_list/", { params }),

    merge: (parentTicketIds: string[]) =>
      apiClient.post("/service_desk/merge_tickets_list/", {
        parent_tickets_ids: parentTicketIds.join(","),
      }),
  };

  inAppNotifications = {
    list: (params?: Record<string, unknown>) =>
      apiClient.get("/in-app-notifications/", { params }),

    markRead: (id: string) =>
      apiClient.post(`/in-app-notifications/${id}/read/`),

    markAllRead: () =>
      apiClient.post("/in-app-notifications/mark-all-read/"),
  };

  priorities = {
    list: () => apiClient.get("/service_desk/priority_listing/"),
  };

  pts = {
    /** Open projects for end-user PTS task form (Select Project dropdown). */
    openProjectList: (config?: AxiosRequestConfig) =>
      apiClient.get("/pts_app/project-summary/open-project-list/", config),

    /** Tasks for a given project (Select Task dropdown). */
    taskList: (projectId: string, config?: AxiosRequestConfig) =>
      apiClient.get("/pts_app/project-task/", {
        params: { project: projectId, is_dashboard: true },
        ...config,
      }),

    /** Work Type dropdown options (GET /dropdown/PTS Work Type/). */
    workTypeList: (config?: AxiosRequestConfig) =>
      apiClient.get("/dropdown/PTS%20Work%20Type/", config),

    /** Create PTS sub-task + ServiceDesk ticket (Save on end-user PTS form). */
    createTask: (
      data: {
        project: string;
        taskname: string;
        sprint: null;
        taskestimatedhours: number;
        parent_task: string;
        status: string;
        work_type: string;
        priority_id: string;
        requester: string;
      },
      config?: AxiosRequestConfig
    ) => apiClient.post("/service_desk/pts_task_create/", data, config),
  };

  /** Formily dynamic-model CRUD — delegates to A1 DynamicModuleService. */
  formily = {
    getGridData: (
      modelName: string,
      page?: number,
      pageSize?: number,
      filterParams?: Record<string, unknown>
    ) => DynamicModuleService.getGridData(modelName, page, pageSize, filterParams),

    getModelListPage: (
      modelName: string,
      page?: number,
      pageSize?: number,
      filterParams?: Record<string, unknown>
    ) =>
      DynamicModuleService.getModelListPage(
        modelName,
        page,
        pageSize,
        filterParams
      ),

    getItemById: (modelName: string, id: string) =>
      DynamicModuleService.getItemById(modelName, id),

    createItem: (modelName: string, payload: DynamicPayload) =>
      DynamicModuleService.createItem(modelName, payload),

    updateItem: (modelName: string, id: string, payload: DynamicPayload) =>
      DynamicModuleService.updateItem(modelName, id, payload),

    deleteItem: (modelName: string, id: string) =>
      DynamicModuleService.deleteItem(modelName, id),

    /** GET grid_data with full Axios response (Admin settings grids). */
    getGridDataResponse: (
      modelName: string,
      page = 1,
      pageSize = 10,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get(`${formilyRecordPath(modelName)}grid_data/`, {
        params: { page, page_size: pageSize },
        ...config,
      }),

    getRecord: (modelName: string, id: string, config?: AxiosRequestConfig) =>
      apiClient.get(formilyRecordPath(modelName, id), config),

    createRecord: (
      modelName: string,
      data: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) => apiClient.post(formilyRecordPath(modelName), data, config),

    patchRecord: (
      modelName: string,
      id: string,
      data: Record<string, unknown>,
      config?: AxiosRequestConfig
    ) => apiClient.patch(formilyRecordPath(modelName, id), data, config),

    deleteRecord: (
      modelName: string,
      id: string,
      config?: AxiosRequestConfig
    ) => apiClient.delete(formilyRecordPath(modelName, id), config),

    getGridDataByModel: (
      modelName: string,
      page = 1,
      pageSize = 2500,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get(`${formilyRecordPath(modelName)}grid_data/`, {
        params: { page, page_size: pageSize },
        ...config,
      }),
  };

  schema = {
    getByName: (schemaName: string) =>
      SchemaService.getSchemaByName(schemaName),

    getColumnGridData: (schemaName: string) =>
      SchemaService.getColumnGridData(schemaName),
  };

  files = {
    getById: (params: { id: string }) => UploadService.getFileById(params),

    getBlob: (path: string, signal?: AbortSignal) =>
      apiClient.get(path, {
        responseType: "blob",
        signal,
        skipErrorNotification: true,
        headers: BLOB_GET_HEADERS,
        transformRequest: [
          (data, headers) => {
            if (headers && typeof headers === "object") {
              delete (headers as Record<string, unknown>)["Content-Type"];
            }
            return data;
          },
        ],
      }) as Promise<AxiosResponse<Blob>>,
  };

  /** Silent list helpers for display-map hooks. */
  lists = {
    agentEmployees: (config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/agent_employee_list/", {
        skipErrorNotification: true,
        ...config,
      }),

    employees: (config?: AxiosRequestConfig) =>
      apiClient.get("/service_desk/employee_list/", {
        skipErrorNotification: true,
        ...config,
      }),

    enduserGrid: (
      page = 1,
      pageSize = 2500,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get(
        `${FORMILY_BASE}/${encodeModelName(SERVICE_DESK_FORMILY_ENDUSER_MODEL)}/grid_data/`,
        {
          params: { page, page_size: pageSize },
          skipErrorNotification: true,
          ...config,
        }
      ),

    customerCompanyGrid: (
      modelName: string,
      page = 1,
      pageSize = 2500,
      config?: AxiosRequestConfig
    ) =>
      apiClient.get(`${formilyRecordPath(modelName)}grid_data/`, {
        params: { page, page_size: pageSize },
        skipErrorNotification: true,
        ...config,
      }),
  };
}

const instance = new ServiceDeskAPIService();

/** Default export — use `ServiceDeskAPIService.auth.login()`, etc. */
export default instance;

/** Backward-compatible alias used across existing components. */
export const ServiceDeskAPI = instance;
